import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Pencil, RefreshCw, Send, Upload } from 'lucide-react';
import { SentMessageModal } from 'components/WhatsApp/SentMessageModal';
import { whatsappService } from 'api/services/whatsapp.service';
import { contactFieldKey } from './contactFieldKey';
import { buildRecipients } from './recipientMapping';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import {
  FormValue, editableFieldsOf, canEdit, isEditable, lockedFields, lockedNote,
  buildPatch, isEmptyPatch, asInt, asText, toDateTimeLocal, epochFromLocal, failureMessage,
} from '../campaignEditRules';

const STATUS_TABS = [
  { key: 'ALL', label: 'All' },
  { key: 'DELIVERED', label: 'Delivered' },
  { key: 'READ', label: 'Read' },
  { key: 'SENT', label: 'Sent' },
  { key: 'FAILED', label: 'Failed' },
];

/**
 * Every field this form knows how to draw, in the order it draws them.
 *
 * Which of them are actually rendered is decided entirely by the campaign's
 * `editableFields` — this table only says what each one LOOKS like. Send rate
 * is first on purpose: it is the field somebody reaches for mid-send, when a
 * campaign is going out faster than the business can answer the replies, and
 * burying it under the template picker would mean pausing instead of throttling.
 */
const EDIT_FIELDS: { name: string; label: string; kind: 'text' | 'textarea' | 'int' | 'datetime' | 'template' | 'phone' | 'json'; help?: string }[] = [
  { name: 'sendRate', label: 'Send rate', kind: 'int',
    help: 'Messages per minute. Takes effect on the next batch — a campaign going too fast can be slowed without pausing it.' },
  { name: 'name', label: 'Campaign name', kind: 'text' },
  { name: 'description', label: 'Description', kind: 'textarea' },
  { name: 'templateId', label: 'Template', kind: 'template' },
  { name: 'phoneNumberId', label: 'Sender number', kind: 'phone' },
  { name: 'scheduledAt', label: 'Scheduled for', kind: 'datetime',
    help: 'Your local time. Clearing the box leaves the existing schedule alone.' },
  { name: 'suppressRecentDays', label: 'Skip numbers messaged in the last', kind: 'int',
    help: 'Days. 0 turns it off. Protects against Meta frequency caps.' },
  { name: 'campaignParameters', label: 'Campaign parameters', kind: 'json',
    help: 'JSON. Applied to every recipient that has no value of its own for a slot.' },
];

const FIELD_LABEL: Record<string, string> = EDIT_FIELDS.reduce(
  (acc, f) => ({ ...acc, [f.name]: f.label }), {} as Record<string, string>,
);

/** The form, as it stands when Edit is opened: current values, every one a string. */
const formFromCampaign = (c: any): Record<string, FormValue> => ({
  name: c?.name ?? '',
  description: c?.description ?? '',
  templateId: c?.templateId ?? '',
  phoneNumberId: c?.phoneNumberId ?? '',
  sendRate: c?.sendRate ?? c?.messagesPerMinute ?? '',
  scheduledAt: toDateTimeLocal(c?.scheduledAt),
  suppressRecentDays: c?.suppressRecentDays ?? '',
  campaignParameters: c?.campaignParameters ?? '',
});

/**
 * Two fields exist to be emptied — a description nobody wants any more, a
 * parameter block being removed. For the rest, blank means "leave it alone"
 * rather than "set it to nothing", because nulling a template id is not an edit
 * anybody means to make.
 */
const CLEARABLE = ['description', 'campaignParameters'];

const toApi = (field: string, value: FormValue): any => {
  if (field === 'scheduledAt') return epochFromLocal(value);
  if (field === 'sendRate' || field === 'suppressRecentDays') return asInt(value);
  if (CLEARABLE.includes(field)) return String(value ?? '').trim();
  return asText(value);
};

const statusPill = (s: string) =>
  s === 'READ' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
  : s === 'DELIVERED' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
  : s === 'FAILED' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';

const WhatsAppCampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<any>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [tab, setTab] = useState('ALL');
  const [rows, setRows] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [viewing, setViewing] = useState<{ id: string; phone: string } | null>(null);
  const [enrolPhone, setEnrolPhone] = useState('');
  const [enrolNote, setEnrolNote] = useState<string | null>(null);
  // Bulk add: the parsed file, and which column is which.
  const [bulkHeaders, setBulkHeaders] = useState<string[]>([]);
  const [bulkRows, setBulkRows] = useState<string[][]>([]);
  const [bulkPhoneCol, setBulkPhoneCol] = useState<number>(-1);
  const [bulkVarMap, setBulkVarMap] = useState<Record<string, number>>({});
  /**
   * Which column is the person's NAME, and which are worth keeping about them.
   *
   * Separate from bulkVarMap, which maps the TEMPLATE's {{1}}/{{2}} slots. Those
   * are argument positions whose meaning lives in the template, so they are never
   * stored on a contact — without these two the file's own name and city columns
   * reached nobody, and the agent that calls the person later had a bare number.
   */
  const [bulkNameCol, setBulkNameCol] = useState<number>(-1);
  const [bulkFieldCols, setBulkFieldCols] = useState<number[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkNote, setBulkNote] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showResend, setShowResend] = useState(false);
  const [rTarget, setRTarget] = useState('FAILED');   // FAILED | REACHED
  const [rError, setRError] = useState('');           // '' = all errors
  const [rMode, setRMode] = useState('ALL');          // ALL | PERCENTAGE | COUNT
  const [rValue, setRValue] = useState(10);
  const [rSampling, setRSampling] = useState('EVEN'); // EVEN | FIRST | RANDOM
  // Responders panel: who replied / clicked a quick-reply button.
  const [respButton, setRespButton] = useState<string>(''); // '' = replied (any)
  const [respButtons, setRespButtons] = useState<string[]>([]);
  const [responders, setResponders] = useState<any[]>([]);
  const [respLoading, setRespLoading] = useState(false);
  // Editing. What may be changed is the server's answer (campaign.editableFields),
  // never a status table kept here — see ../campaignEditRules.
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, FormValue>>({});
  const [editInitial, setEditInitial] = useState<Record<string, FormValue>>({});
  const [editError, setEditError] = useState<string | null>(null);
  const [editNote, setEditNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [numbers, setNumbers] = useState<any[]>([]);
  // The throttle, reachable without opening anything. Separate draft state so
  // typing in it does not fight the modal's copy of the same field.
  const [rateDraft, setRateDraft] = useState('');
  const [rateBusy, setRateBusy] = useState(false);
  const [rateNote, setRateNote] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      const c = await whatsappService.getCampaign(id);
      if (c.success && c.data) setCampaign(c.data);
      // Quick-reply button labels for the responders filter.
      const ins = await whatsappService.getCampaignInsights(id);
      if (ins.success && ins.data?.quickReplyClicks) {
        setRespButtons(ins.data.quickReplyClicks.map((b: any) => b.label).filter(Boolean));
      }
      const cs: Record<string, number> = {};
      await Promise.all(STATUS_TABS.map(async (t) => {
        const r = await whatsappService.getCampaignRecipients(id, { status: t.key, page: 0, size: 1 });
        cs[t.key] = r.success && r.data ? (r.data.totalElements ?? 0) : 0;
      }));
      setCounts(cs);
      setLoading(false);
    })();
  }, [id]);

  const loadTab = useCallback(async (status: string, p = 0, searchTerm = '') => {
    if (!id) return;
    setRowsLoading(true);
    const r = await whatsappService.getCampaignRecipients(id, { status, search: searchTerm || undefined, page: p, size: 50 });
    if (r.success && r.data) { setRows(r.data.content || []); setPage(r.data.page ?? p); setTotalPages(r.data.totalPages ?? 0); }
    else { setRows([]); setTotalPages(0); }
    setRowsLoading(false);
  }, [id]);

  useEffect(() => { if (id && !loading) loadTab(tab, 0, search); }, [tab, loading, id, loadTab]);

  const loadResponders = useCallback(async (button: string) => {
    if (!id) return;
    setRespLoading(true);
    const r = await whatsappService.getCampaignResponders(id, button || undefined);
    setResponders(r.success && r.data ? r.data : []);
    setRespLoading(false);
  }, [id]);

  useEffect(() => { if (id && !loading) loadResponders(respButton); }, [respButton, loading, id, loadResponders]);

  /**
   * Re-read the campaign after a change, so the page shows what the server now
   * holds — including a refreshed `editableFields`, which a status change (a
   * schedule that has just started sending) will have narrowed.
   */
  const reloadCampaign = useCallback(async () => {
    if (!id) return;
    const c = await whatsappService.getCampaign(id);
    if (c.success && c.data) setCampaign(c.data);
  }, [id]);

  // Keep the inline throttle showing the live value, except while it is being
  // typed into — overwriting a half-typed number mid-keystroke is maddening.
  const liveRate = (campaign as any)?.sendRate ?? (campaign as any)?.messagesPerMinute;
  useEffect(() => {
    if (rateBusy) return;
    setRateDraft(liveRate === null || liveRate === undefined ? '' : String(liveRate));
    // Deliberately keyed on the server's value alone: a new value from the
    // server replaces the draft, a re-render does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveRate]);

  const exportResponders = () => {
    if (!id) return;
    const label = respButton || 'replied';
    const csv = ['phone,name,responded_at', ...responders.map((r) =>
      `${r.phone || ''},"${(r.name || '').replace(/"/g, '""')}",${r.respondedAt ? new Date(r.respondedAt).toISOString() : ''}`)].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = `campaign-${id}-responders-${label}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportCsv = async () => {
    if (!id) return;
    const res = await whatsappService.getCampaignRecipients(id, { status: tab, search: search || undefined, page: 0, size: 100000 });
    const data: any[] = res.success && res.data ? (res.data.content || []) : [];
    const csv = ['phone,status,reason', ...data.map((r) => `${r.phone || ''},${r.status || ''},"${(r.errorReason || '').replace(/"/g, '""')}"`)].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = `campaign-${id}-${tab.toLowerCase()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // Eligible recipients for the current resend selection (before volume limit).
  const eligibleCount = (): number => {
    if (rTarget === 'REACHED') return (counts.DELIVERED || 0) + (counts.READ || 0);
    if (rError) {
      const f = campaign?.insights?.failureBreakdown?.find((x: any) => String(x.code) === rError);
      return f ? f.count : 0;
    }
    return counts.FAILED || 0;
  };
  const resendCount = (): number => {
    const elig = eligibleCount();
    if (rMode === 'PERCENTAGE') return Math.ceil((elig * Math.max(1, Math.min(100, rValue))) / 100);
    if (rMode === 'COUNT') return Math.min(elig, Math.max(1, rValue));
    return elig;
  };

  const doResend = async () => {
    if (!id) return;
    setResending(true);
    const res = await whatsappService.resendFailed(id, {
      target: rTarget,
      errorCode: rTarget === 'FAILED' && rError ? rError : undefined,
      mode: rMode,
      value: rMode === 'ALL' ? undefined : rValue,
      sampling: rSampling,
    });
    setResending(false);
    if (res.success) navigate('/whatsapp/campaigns');
    else alert(res.error || 'Resend failed');
  };

  if (loading) return <div className="p-6"><LoadingSpinner /></div>;
  if (!campaign) return <div className="p-6 text-gray-500">Campaign not found.</div>;

  const stat = (k: string) => campaign[k] ?? 0;
  const insights = campaign.insights;

  const enrol = async () => {
    if (!id || !enrolPhone.trim()) return;
    setEnrolNote(null);
    const r = await whatsappService.enrolRecipient(id, { phone: enrolPhone.trim() });
    // PENDING, not "sent". The scheduler wakes the campaign within a minute, and the
    // person reading this is deciding whether to chase it.
    setEnrolNote(r.success
      ? `${enrolPhone.trim()} added — queued, the scheduler picks it up within a minute.`
      : (r.error || 'Could not add that number'));
    if (r.success) { setEnrolPhone(''); }
  };

  // ── editing ───────────────────────────────────────────────────────────────
  const editable = editableFieldsOf(campaign);
  const rateEditable = isEditable(campaign, 'sendRate');

  /** Only the fields the server offered, in this form's own order. */
  const shownFields = EDIT_FIELDS.filter((f) => editable.includes(f.name));
  /** ...and what it withheld, so the missing controls are accounted for. */
  const lockedNames = lockedFields(EDIT_FIELDS.map((f) => f.name), editable);
  const whyLocked = lockedNote(campaign?.status, lockedNames.map((n) => FIELD_LABEL[n]));

  const openEdit = async () => {
    const start = formFromCampaign(campaign);
    setEditForm(start);
    setEditInitial(start);
    setEditError(null);
    setShowEdit(true);
    // Only fetched when the field that needs them is actually on offer — a
    // RUNNING campaign cannot change either, so neither list is worth a request.
    if (editable.includes('templateId') && templates.length === 0) {
      const t = await whatsappService.getApprovedTemplates();
      setTemplates(t.success && Array.isArray(t.data) ? t.data : []);
    }
    if (editable.includes('phoneNumberId') && numbers.length === 0) {
      const n = await whatsappService.getAllConfigs();
      setNumbers(n.success && Array.isArray(n.data) ? n.data : []);
    }
  };

  const saveEdit = async () => {
    if (!id) return;
    setEditError(null);

    // Caught here rather than at the server only because the server's answer to
    // broken JSON is a parse error, which says nothing about which box to fix.
    const params = String(editForm.campaignParameters ?? '').trim();
    if (editable.includes('campaignParameters') && params !== '') {
      try { JSON.parse(params); }
      catch { setEditError('Campaign parameters must be valid JSON.'); return; }
    }

    const body = buildPatch(editInitial, editForm, editable, toApi);
    if (isEmptyPatch(body)) { setEditError('Nothing has been changed yet.'); return; }

    setSaving(true);
    const res = await whatsappService.updateCampaign(id, body);
    setSaving(false);
    // The server names the field it refused; repeating "Update failed" instead
    // would throw away the only part of the answer worth reading.
    if (!res.success) { setEditError(failureMessage(res, 'Could not save those changes')); return; }
    setShowEdit(false);
    setEditNote(`Saved: ${Object.keys(body).map((k) => FIELD_LABEL[k] || k).join(', ')}.`);
    await reloadCampaign();
  };

  /** The throttle on its own — one number, one click, no modal. */
  const applyRate = async () => {
    if (!id) return;
    setRateNote(null);
    const body = buildPatch({ sendRate: liveRate }, { sendRate: rateDraft }, ['sendRate'], toApi);
    if (isEmptyPatch(body)) { setRateNote('That is already the send rate.'); return; }
    setRateBusy(true);
    const res = await whatsappService.updateCampaign(id, body);
    setRateBusy(false);
    if (!res.success) { setRateNote(failureMessage(res, 'Could not change the send rate')); return; }
    setRateNote(`Now sending at ${body.sendRate} a minute.`);
    await reloadCampaign();
  };

  /**
   * The template's {{n}} slots, in body order.
   *
   * A plain computation, deliberately NOT useMemo: everything below here sits
   * after `if (loading) return` and `if (!campaign) return`, so a hook placed
   * here runs on some renders and not others — React counts a different number
   * of hooks once the campaign arrives and the whole page white-screens. It is
   * a regex over one short string; there is nothing to memoise.
   */
  const templateSlots: string[] = (() => {
    const body: string = campaign?.templateBody || campaign?.bodyText || '';
    const found = (body.match(/\{\{\s*(\w+)\s*\}\}/g) || [])
      .map((m: string) => m.replace(/[{}]/g, '').trim())
      .filter((t: string) => /^[0-9]+$/.test(t));
    return Array.from(new Set(found));
  })();

  /** Which column is which, as the operator mapped them below. */
  const bulkMapping = {
    headers: bulkHeaders,
    phoneCol: bulkPhoneCol,
    slots: templateSlots,
    varMap: bulkVarMap,
    nameCol: bulkNameCol,
    fieldCols: bulkFieldCols,
  };
  const bulkValid = bulkRows.length > 0 && bulkPhoneCol >= 0 ? buildRecipients(bulkRows, bulkMapping).length : 0;

  const onBulkFile = async (file?: File) => {
    if (!file) return;
    setBulkNote(null);
    setBulkBusy(true);
    const r = await whatsappService.parseRecipientFile(file);
    setBulkBusy(false);
    if (!r.success || !r.data) { setBulkNote(r.error || 'Could not read that file'); return; }
    const { headers: hs, rows: rs, totalRows, truncated } = r.data;
    if (!hs?.length) { setBulkNote('That file has no header row'); return; }
    setBulkHeaders(hs);
    setBulkRows(rs.map((row: any) => hs.map((h: string) => row[h] ?? '')));
    const guess = hs.findIndex((h: string) => /phone|mobile|number|msisdn|contact/i.test(h));
    setBulkPhoneCol(guess >= 0 ? guess : 0);
    setBulkVarMap({});
    setBulkNameCol(-1);
    setBulkFieldCols([]);
    // Never silent about a shortened list.
    setBulkNote(truncated
      ? `Read ${rs.length.toLocaleString()} of ${totalRows.toLocaleString()} rows — the rest were not loaded.`
      : `${rs.length.toLocaleString()} rows read.`);
  };

  const addBulk = async () => {
    if (!id || bulkPhoneCol < 0 || bulkRows.length === 0) return;
    setBulkBusy(true);
    // The same mapping the campaign builder uses, from the same module: the
    // number is normalised, the name and the chosen columns ride along on the
    // person, and a cell too short to be a number is dropped rather than sent.
    const recipients = buildRecipients(bulkRows, bulkMapping);
    const res = await whatsappService.enrolRecipientsBulk(id, recipients);
    setBulkBusy(false);
    if (!res.success) { setBulkNote(res.error || res.message || 'Could not add that file'); return; }
    const d = res.data || {};
    setBulkHeaders([]); setBulkRows([]); setBulkPhoneCol(-1);
    setBulkNameCol(-1); setBulkFieldCols([]);

    if (d.status === 'ACCEPTED') {
      // The server took the file and is enrolling behind the request, because a
      // big file used to run past the load balancer's timeout and report a
      // failure it had not had — after which the obvious retry queued everybody
      // twice. There are no per-row counts yet, so say what is true and let the
      // list below fill in, rather than printing three zeroes that read as
      // nothing having happened.
      setBulkNote(`Adding ${(d.accepted ?? recipients.length).toLocaleString()} rows — they appear below as they land. Numbers already waiting are skipped.`);
      // A few refreshes rather than one: the work takes seconds, and a single
      // immediate reload would show the list exactly as it was and look broken.
      [1000, 4000, 10000, 20000].forEach((ms) => setTimeout(() => loadTab(tab, 0, search), ms));
      return;
    }

    // All three numbers, always. A count of what went in, with no mention of
    // what did not, is how somebody believes a whole list was added.
    setBulkNote(`${d.enrolled ?? 0} added, ${d.skipped ?? 0} already waiting, ${d.failed ?? 0} could not be added.`);
    // Refresh the recipient list so the people just added are visible, rather
    // than leaving a count the reader has to take on trust.
    loadTab(tab, 0, search);
  };

  return (
    <div className="p-6 max-w-5xl space-y-6">
      <button onClick={() => navigate('/whatsapp/campaigns')} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Campaigns
      </button>

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{campaign.name}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Template <b>{campaign.templateName}</b> · {campaign.totalRecipients ?? 0} recipients · {campaign.status}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Shown only when the server says something can be changed. An empty
              or absent editableFields means no edit control at all. */}
          {canEdit(campaign) && (
            <button onClick={openEdit}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-sm">
              <Pencil className="w-4 h-4" /> Edit
            </button>
          )}
          {((counts.FAILED ?? 0) > 0 || ((counts.DELIVERED ?? 0) + (counts.READ ?? 0)) > 0) && (
            <button onClick={() => setShowResend(true)}
              className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2 text-sm">
              <Send className="w-4 h-4" /> Resend…
            </button>
          )}
        </div>
      </div>

      {editNote && (
        <p className="text-sm text-green-700 dark:text-green-400">{editNote}</p>
      )}

      {/* The throttle, in the open.
          Slowing a campaign that is going out faster than anyone can answer is
          the edit people need in a hurry, and the alternative they reach for
          when they cannot find it is Pause — which stops the campaign instead of
          slowing it. So it sits here rather than behind Edit. */}
      {rateEditable && (
        <div className="flex flex-wrap items-center gap-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
          <label htmlFor="wa-send-rate" className="text-sm font-medium text-gray-700 dark:text-gray-300">Send rate</label>
          <input
            id="wa-send-rate"
            type="number"
            min={1}
            max={300}
            value={rateDraft}
            onChange={(e) => setRateDraft(e.target.value)}
            className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded text-sm"
          />
          <span className="text-sm text-gray-500 dark:text-gray-400">messages / minute</span>
          <button
            onClick={applyRate}
            disabled={rateBusy || rateDraft.trim() === ''}
            className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-50"
          >
            {rateBusy ? 'Applying…' : 'Apply'}
          </button>
          {rateNote && <span className="text-sm text-gray-600 dark:text-gray-300">{rateNote}</span>}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Sent', val: stat('messagesSent') || stat('sentCount'), color: 'text-blue-600' },
          { label: 'Delivered', val: stat('messagesDelivered') || stat('deliveredCount'), color: 'text-green-600' },
          { label: 'Read', val: stat('messagesRead') || stat('readCount'), color: 'text-purple-600' },
          { label: 'Failed', val: stat('messagesFailed') || stat('failedCount'), color: 'text-red-600' },
        ].map((c) => (
          <div key={c.label} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">{c.label}</p>
            <p className={`text-2xl font-bold ${c.color}`}>{c.val}</p>
          </div>
        ))}
      </div>

      {/* Insights */}
      {insights && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <div className="flex flex-wrap gap-6 text-sm">
            <span>Delivery rate: <b className="text-green-600">{insights.deliveryRatePct ?? 0}%</b></span>
            <span>Read rate: <b className="text-purple-600">{insights.readRatePct ?? 0}%</b></span>
            <span>Pending: <b>{insights.pending ?? 0}</b></span>
          </div>
          {(insights.failureBreakdown?.length ?? 0) > 0 && (
            <div className="space-y-1">
              {insights.failureBreakdown.map((f: any) => (
                <div key={f.code} className="flex justify-between text-sm bg-red-50 dark:bg-red-900/20 rounded px-3 py-1.5">
                  <span><span className="font-mono text-xs text-red-600 mr-2">{f.code}</span>{f.reason}</span>
                  <span className="font-semibold text-red-700 dark:text-red-300">{f.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Responders — who replied / clicked a quick-reply button */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-semibold text-gray-900 dark:text-white">Responders</h3>
          <div className="flex items-center gap-2">
            <select
              value={respButton}
              onChange={(e) => setRespButton(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700"
            >
              <option value="">Replied (any)</option>
              {respButtons.map((b) => <option key={b} value={b}>Clicked “{b}”</option>)}
            </select>
            <button
              onClick={exportResponders}
              disabled={responders.length === 0}
              className="text-sm px-3 py-1 rounded bg-green-600 text-white disabled:opacity-50"
            >
              Export CSV
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500">
          {respLoading ? 'Loading…' : `${responders.length} ${respButton ? `clicked “${respButton}”` : 'replied'}`}
        </p>
        {responders.length > 0 && (
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-1">Phone</th><th>Name</th><th>Responded</th>
                </tr>
              </thead>
              <tbody>
                {responders.map((r, i) => (
                  <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="py-1 font-mono">{r.phone}</td>
                    <td>{r.name || '—'}</td>
                    <td className="text-gray-500">{r.respondedAt ? new Date(r.respondedAt).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Timeline</h3>
        <div className="space-y-1.5 text-sm">
          {[
            ['Created', campaign.createdAt],
            ['Scheduled', campaign.scheduledAt],
            ['Started', campaign.startedAt],
            ['Completed', campaign.completedAt],
          ].filter(([, v]) => v).map(([label, v]) => (
            <div key={label as string} className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">{label}</span>
              <span className="text-gray-800 dark:text-gray-200">{new Date(v as number).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>

      {/* A standing campaign is a bucket, not a run — say so where somebody would
          otherwise wait for it to finish. */}
      {campaign.standing && (
        <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-xl p-5 space-y-3">
          <div>
            <h2 className="font-medium text-violet-900 dark:text-violet-200">Standing campaign</h2>
            <p className="text-sm text-violet-800/80 dark:text-violet-300/80 mt-1">
              This one stays open and never completes. Add numbers one at a time — from here, from a
              form, or from an agent after a call — and each is sent with the same DND checks,
              recency suppression and delivery reporting as a bulk run.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="tel"
              value={enrolPhone}
              onChange={(e) => setEnrolPhone(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') enrol(); }}
              placeholder="+919876543210"
              className="flex-1 min-w-[16rem] px-3 py-2 text-sm rounded-lg border border-violet-300 dark:border-violet-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
            <button
              onClick={enrol}
              disabled={!enrolPhone.trim()}
              className="px-3 py-2 text-sm rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
            >
              Add to campaign
            </button>
          </div>
          {enrolNote && <p className="text-sm text-violet-900 dark:text-violet-200">{enrolNote}</p>}

          {/* ── or a whole file ───────────────────────────────────────────── */}
          <div className="pt-3 border-t border-violet-200 dark:border-violet-800 space-y-3">
            <div>
              <h3 className="text-sm font-medium text-violet-900 dark:text-violet-200">Or add a whole file</h3>
              <p className="text-xs text-violet-800/80 dark:text-violet-300/80 mt-1">
                CSV or Excel. Everyone in it becomes a contact, exactly as they would in a new
                campaign — and anyone already waiting to be sent is skipped rather than messaged twice.
              </p>
            </div>

            <label className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-violet-300 dark:border-violet-700 cursor-pointer bg-white dark:bg-gray-800">
              <Upload className="w-4 h-4" /> {bulkBusy ? 'Working…' : 'Choose file'}
              <input
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                disabled={bulkBusy}
                onChange={(e) => { onBulkFile(e.target.files?.[0]); e.target.value = ''; }}
              />
            </label>

            {bulkHeaders.length > 0 && (
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-3">
                  <span className="w-40 text-violet-900 dark:text-violet-200">Phone number →</span>
                  <select
                    value={bulkPhoneCol}
                    onChange={(e) => setBulkPhoneCol(Number(e.target.value))}
                    className="px-2 py-1.5 rounded border border-violet-300 dark:border-violet-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  >
                    <option value={-1}>Choose a column…</option>
                    {bulkHeaders.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                  </select>
                </div>

                {templateSlots.map((tok) => (
                  <div key={tok} className="flex items-center gap-3">
                    <span className="w-40 text-violet-900 dark:text-violet-200">Variable {`{{${tok}}}`} →</span>
                    <select
                      value={bulkVarMap[tok] ?? -1}
                      onChange={(e) => setBulkVarMap((m) => ({ ...m, [tok]: Number(e.target.value) }))}
                      className="px-2 py-1.5 rounded border border-violet-300 dark:border-violet-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    >
                      <option value={-1}>Leave to the campaign default</option>
                      {bulkHeaders.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                    </select>
                  </div>
                ))}
                {templateSlots.length > 0 && (
                  <p className="text-xs text-violet-800/80 dark:text-violet-300/80 max-w-2xl">
                    A variable left unmapped is filled from this campaign's defaults — WhatsApp refuses
                    a template with an empty slot, so something has to go there.
                  </p>
                )}

                {/* ── what to remember about these people ─────────────────── */}
                <div className="pt-3 mt-1 border-t border-violet-200 dark:border-violet-800 space-y-3">
                  <div>
                    <h4 className="text-sm font-medium text-violet-900 dark:text-violet-200">
                      What to remember about these people <span className="font-normal text-violet-800/60 dark:text-violet-300/60">(optional)</span>
                    </h4>
                    <p className="text-xs text-violet-800/80 dark:text-violet-300/80 mt-1 max-w-2xl">
                      Everyone added becomes a contact. The variables above fill the template —
                      <b> {'{{1}}'}, {'{{2}}'}</b> and so on are argument slots whose meaning changes with
                      the template, so they are not kept on the person. Say here which columns are about
                      <i> them</i>, and the agent that calls them later will know it.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-40 text-violet-900 dark:text-violet-200">Their name →</span>
                    <select
                      value={bulkNameCol}
                      onChange={(e) => setBulkNameCol(Number(e.target.value))}
                      className="px-2 py-1.5 rounded border border-violet-300 dark:border-violet-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    >
                      <option value={-1}>Not in this file</option>
                      {bulkHeaders.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                    </select>
                  </div>
                  <div>
                    <span className="block text-violet-900 dark:text-violet-200 mb-1">Also keep</span>
                    <div className="flex flex-wrap gap-2">
                      {bulkHeaders.map((h, i) => {
                        const key = contactFieldKey(h, i);
                        const on = bulkFieldCols.includes(i);
                        // A column that cannot be a field is shown disabled WITH the
                        // reason: silently omitting it reads as a rendering bug.
                        const why = !key
                          ? (/^[0-9]+$/.test((h || '').trim()) ? 'a template slot, not a fact'
                             : (h || '').trim() ? 'reserved by the template sender' : 'unnamed column')
                          : null;
                        return (
                          <button
                            key={i}
                            type="button"
                            disabled={!key || i === bulkNameCol || i === bulkPhoneCol}
                            title={why || (i === bulkNameCol ? 'kept as their name' : i === bulkPhoneCol ? 'kept as their number' : `stored as ${key}`)}
                            onClick={() => setBulkFieldCols((c) => c.includes(i) ? c.filter((x) => x !== i) : [...c, i])}
                            className={`px-2 py-1 rounded border text-xs ${
                              on ? 'bg-green-50 border-green-300 text-green-800 dark:bg-green-900/20 dark:text-green-200'
                                 : 'border-violet-300 dark:border-violet-700 text-violet-800 dark:text-violet-200'
                            } disabled:opacity-40 disabled:cursor-not-allowed`}
                          >
                            {h || `Column ${i + 1}`}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* The count AFTER mapping, not the row count: a file whose phone
                    column holds serial numbers reads "1,200 rows" and adds nobody. */}
                <p className="text-xs text-violet-800/80 dark:text-violet-300/80">
                  Valid recipients after mapping: <b>{bulkValid.toLocaleString()}</b>
                  {bulkValid < bulkRows.length && <span> of {bulkRows.length.toLocaleString()} rows</span>}
                </p>

                <button
                  type="button"
                  onClick={addBulk}
                  disabled={bulkBusy || bulkPhoneCol < 0 || bulkValid === 0}
                  className="px-3 py-2 text-sm rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
                >
                  {bulkBusy ? 'Adding…' : `Add ${bulkValid.toLocaleString()} to campaign`}
                </button>
              </div>
            )}

            {bulkNote && <p className="text-sm text-violet-900 dark:text-violet-200">{bulkNote}</p>}
          </div>
        </div>
      )}

      {/* Recipients with status tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-4 flex-wrap">
          <div className="flex gap-1 overflow-x-auto">
            {STATUS_TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${
                  tab === t.key ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}>
                {t.label} <span className="ml-1 text-xs text-gray-400">{counts[t.key] ?? 0}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 py-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') loadTab(tab, 0, search); }}
              placeholder="Search number…"
              className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded w-36"
            />
            {search && (
              <button onClick={() => { setSearch(''); loadTab(tab, 0, ''); }} className="text-xs text-gray-400 hover:text-gray-600">clear</button>
            )}
            <button onClick={() => loadTab(tab, page, search)} className="text-xs px-2 py-1 text-gray-500 hover:text-gray-700 flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
            <button onClick={exportCsv} className="text-xs px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center gap-1">
              <Download className="w-3 h-3" /> Export
            </button>
          </div>
        </div>

        <div className="p-2">
          {rowsLoading ? (
            <p className="p-4 text-sm text-gray-400">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="p-4 text-sm text-gray-400">No recipients in this segment.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="px-3 py-2">Phone</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Variables</th>
                  <th className="px-3 py-2">Reason</th>
                  <th className="px-3 py-2 text-right">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map((r, i) => {
                  const vars = r.variables && Object.keys(r.variables).length
                    ? Object.keys(r.variables).sort((a, b) => Number(a) - Number(b)).map((k) => `${k}: ${r.variables[k]}`).join(' · ')
                    : '';
                  return (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-3 py-1.5 font-mono text-xs text-gray-700 dark:text-gray-300">{r.phone}</td>
                    <td className="px-3 py-1.5"><span className={`text-xs px-2 py-0.5 rounded-full ${statusPill(r.status)}`}>{r.status}</span></td>
                    <td className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 max-w-[220px] truncate" title={vars}>{vars || '—'}</td>
                    <td className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400">{r.errorReason || '—'}</td>
                    <td className="px-3 py-1.5 text-right">
                      {/* Only where there is something to show — a row with no id, or one
                          still pending, has no stored message behind the link. */}
                      {r.id && (r.status === 'SENT' || r.status === 'FAILED') ? (
                        <button
                          onClick={() => setViewing({ id: r.id, phone: r.phone })}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          View sent
                        </button>
                      ) : null}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-2 text-xs text-gray-500 border-t border-gray-100 dark:border-gray-700">
            <button disabled={page <= 0} onClick={() => loadTab(tab, page - 1)} className="px-2 py-1 rounded disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700">Prev</button>
            <span>Page {page + 1} of {totalPages}</span>
            <button disabled={page >= totalPages - 1} onClick={() => loadTab(tab, page + 1)} className="px-2 py-1 rounded disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700">Next</button>
          </div>
        )}
      </div>

      {/* Targeted resend modal */}
      {showResend && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowResend(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Targeted resend</h3>

            {/* Target */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Target</label>
              <div className="flex gap-2">
                {[['FAILED', `Failed (${counts.FAILED || 0})`], ['REACHED', `Reached (${(counts.DELIVERED || 0) + (counts.READ || 0)})`]].map(([v, label]) => (
                  <button key={v} onClick={() => { setRTarget(v); setRError(''); }}
                    className={`px-3 py-1.5 rounded-lg text-sm border ${rTarget === v ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Error code (FAILED only) */}
            {rTarget === 'FAILED' && (campaign?.insights?.failureBreakdown?.length ?? 0) > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Failure reason</label>
                <select value={rError} onChange={(e) => setRError(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm">
                  <option value="">All failures ({counts.FAILED || 0})</option>
                  {campaign.insights.failureBreakdown.map((f: any) => (
                    <option key={f.code} value={String(f.code)}>{f.code} — {f.reason} ({f.count})</option>
                  ))}
                </select>
              </div>
            )}

            {/* Volume */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">How many</label>
              <div className="flex items-center gap-2">
                <select value={rMode} onChange={(e) => setRMode(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm">
                  <option value="ALL">All eligible</option>
                  <option value="PERCENTAGE">Percentage</option>
                  <option value="COUNT">Fixed count</option>
                </select>
                {rMode !== 'ALL' && (
                  <input type="number" min={1} value={rValue} onChange={(e) => setRValue(Number(e.target.value))}
                    className="w-24 px-2 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm" />
                )}
                {rMode === 'PERCENTAGE' && <span className="text-sm text-gray-500">%</span>}
              </div>
            </div>

            {/* Sampling */}
            {rMode !== 'ALL' && (
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Pick</label>
                <div className="flex gap-2">
                  {[['EVEN', 'Evenly spread'], ['FIRST', 'First N'], ['RANDOM', 'Random']].map(([v, label]) => (
                    <button key={v} onClick={() => setRSampling(v)}
                      className={`px-3 py-1.5 rounded-lg text-xs border ${rSampling === v ? 'bg-gray-700 text-white border-gray-700' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <p className="text-sm text-gray-600 dark:text-gray-300">
              Will resend to <b>{resendCount()}</b> of {eligibleCount()} {rTarget === 'REACHED' ? 'reached' : 'failed'} recipients.
            </p>

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setShowResend(false)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
              <button onClick={doResend} disabled={resending || resendCount() < 1}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 text-sm flex items-center gap-2">
                <Send className="w-4 h-4" /> {resending ? 'Launching…' : `Resend ${resendCount()}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal — same shape as the resend modal above, because a second
          modal pattern on one page is a second thing to learn. */}
      {showEdit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowEdit(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Edit campaign</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {campaign.status} · only what you change is sent.
              </p>
            </div>

            {shownFields.map((f) => (
              <div key={f.name}>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{f.label}</label>

                {f.kind === 'textarea' ? (
                  <textarea
                    rows={2}
                    value={String(editForm[f.name] ?? '')}
                    onChange={(e) => setEditForm({ ...editForm, [f.name]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm"
                  />
                ) : f.kind === 'json' ? (
                  <textarea
                    rows={3}
                    value={String(editForm[f.name] ?? '')}
                    onChange={(e) => setEditForm({ ...editForm, [f.name]: e.target.value })}
                    placeholder='{"1": "Diwali"}'
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-xs font-mono"
                  />
                ) : f.kind === 'template' ? (
                  <select
                    value={String(editForm[f.name] ?? '')}
                    onChange={(e) => setEditForm({ ...editForm, [f.name]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm"
                  >
                    {/* The campaign's own template first, so a list that has not
                        arrived yet still shows what is set rather than blank. */}
                    <option value={String(editInitial.templateId ?? '')}>
                      {campaign.templateName || 'Current template'}
                    </option>
                    {templates
                      .filter((t: any) => String(t.id) !== String(editInitial.templateId ?? ''))
                      .map((t: any) => <option key={t.id} value={t.id}>{t.name || t.templateName}</option>)}
                  </select>
                ) : f.kind === 'phone' ? (
                  <select
                    value={String(editForm[f.name] ?? '')}
                    onChange={(e) => setEditForm({ ...editForm, [f.name]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm"
                  >
                    <option value="">Organisation's primary number</option>
                    {numbers.map((n: any) => (
                      <option key={n.phoneNumberId} value={n.phoneNumberId}>
                        {n.label || n.displayName || n.phoneNumber || n.phoneNumberId}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={f.kind === 'int' ? 'number' : f.kind === 'datetime' ? 'datetime-local' : 'text'}
                    min={f.kind === 'int' ? 0 : undefined}
                    value={String(editForm[f.name] ?? '')}
                    onChange={(e) => setEditForm({ ...editForm, [f.name]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm"
                  />
                )}

                {f.help && <p className="text-xs text-gray-400 mt-1">{f.help}</p>}
              </div>
            ))}

            {shownFields.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                The server offers no fields this screen knows how to edit.
              </p>
            )}

            {/* Somebody who came here to change the template and cannot find the
                field should not be left wondering whether it is hidden, broken,
                or on another page. */}
            {whyLocked && (
              <p className="text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-3">
                {whyLocked}
              </p>
            )}

            {editError && <p className="text-sm text-red-600 dark:text-red-400">{editError}</p>}

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setShowEdit(false)}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                Cancel
              </button>
              <button onClick={saveEdit} disabled={saving || shownFields.length === 0}
                className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-50">
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewing && id && (
        <SentMessageModal
          campaignId={id}
          recipientId={viewing.id}
          phone={viewing.phone}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
};

export default WhatsAppCampaignDetail;
